import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  List,
  Divider,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Sidebar } from './Sidebar';
import {
  getSidebarIconCenterX,
  TOGGLE_SIZE,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from './Sidebar';
import { NavList } from './NavList';
import { Header, HEADER_HEIGHT } from './Header';
import { darkSurfaces } from '@/utils/themeBuilder';
import { MobileNav } from './MobileNav';
import { Breadcrumbs } from './Breadcrumbs';
import { OnboardingPrompt } from '@/features/onboarding/OnboardingPrompt';
import { SessionTimeout } from '@/components/auth/SessionTimeout';
import {
  AssistantPanel,
  AssistantContextProvider,
  ASSISTANT_PANEL_WIDTH,
  useAssistantSideBySide,
} from '@/components/assistant';
import { useUIStore } from '@/stores/uiStore';
import { useAssistantStore } from '@/stores/assistantStore';
import { useBranding } from '@/hooks/useBranding';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCurrentUser } from '@/hooks/useAuth';
import { AppVersionButton } from '@/features/releases/AppVersionButton';
import { VersionUpdateNotice } from '@/features/releases/VersionUpdateNotice';
import { WhatsNewDialog } from '@/features/releases/WhatsNewDialog';

interface AppShellProps {
  children: ReactNode;
}

// Routes that should fill the viewport edge-to-edge — no max-width cap, an even
// 16px gutter to the browser edges, and the page owning its own vertical scroll
// (rather than the padded, max-width-capped, page-scrolling default). The
// calendar sizes itself to the remaining height so its bottom reaches the
// viewport bottom minus the gutter.
const FULL_BLEED_PATHS = ['/calendar'];

export function AppShell({ children }: AppShellProps) {
  const isMobile = useIsMobile();
  const { t: tc } = useTranslation('common');
  const location = useLocation();
  const isFullBleed = FULL_BLEED_PATHS.includes(location.pathname);
  // Full-bleed fill-height only applies on desktop; mobile keeps the default
  // padded, scrolling layout (bottom nav / safe-area reservations stay intact).
  const fullBleedDesktop = isFullBleed && !isMobile;
  const mobileMenuOpen = useUIStore((s) => s.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const fullWidthLayout = useUIStore((s) => s.fullWidthLayout);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { companyName } = useBranding();

  // Same side-by-side check as Header/AssistantPanel: the assistant only
  // reserves horizontal space when it renders as a docked panel, so the fixed
  // top divider must stop at its edge instead of running beneath/over it.
  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH;
  const isAssistantOpen = useAssistantStore((s) => s.isOpen);
  const assistantSideBySide = useAssistantSideBySide(sidebarWidth);
  const assistantWidth = isAssistantOpen && assistantSideBySide ? ASSISTANT_PANEL_WIDTH : 0;

  // Sync user data (permissions, role) from server on mount
  useCurrentUser();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', maxWidth: '100vw', overflow: 'hidden' }}>
      {/* Skip to main content link */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          padding: '8px',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          textDecoration: 'none',
          '&:focus': {
            left: 0,
            top: 0,
          },
        }}
      >
        {tc('skipToMainContent')}
      </Box>

      {/* Sidebar - hidden on mobile */}
      {!isMobile && (
        <Box component="nav" aria-label={tc('mainNavigation')}>
          <Sidebar />
        </Box>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{ sx: { width: 280 } }}
        >
          <Box sx={{ px: 2, py: 2, minHeight: 64, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              {companyName}
            </Typography>
          </Box>
          <Divider />
          <List sx={{ px: 1, py: 2 }}>
            <NavList
              variant="drawer"
              includeSettings
              onItemClick={() => setMobileMenuOpen(false)}
            />
          </List>
          <Box sx={{ px: 3, pb: 2 }}>
            <AppVersionButton onClick={() => setMobileMenuOpen(false)} />
          </Box>
        </Drawer>
      )}

      {/* Top divider + expand/collapse toggle, rendered once here as fixed
          elements so both are anchored to the same layout coordinates (the divider
          at HEADER_HEIGHT, the toggle on the nav-icon center line). Desktop only. */}
      {!isMobile && (
        <>
          <Box
            aria-hidden
            sx={{
              position: 'fixed',
              top: `${String(HEADER_HEIGHT - 1)}px`,
              left: 0,
              right: assistantWidth,
              height: '1px',
              bgcolor: 'divider',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              pointerEvents: 'none',
              transition: (theme) =>
                theme.transitions.create('right', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            }}
          />
          <Tooltip title={sidebarCollapsed ? tc('expand') : tc('collapse')}>
          <IconButton
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? tc('expand') : tc('collapse')}
            sx={{
              position: 'fixed',
              top: `${String(HEADER_HEIGHT)}px`,
              left: `${String(getSidebarIconCenterX(sidebarCollapsed))}px`,
              transform: 'translate(-50%, -50%)',
              width: TOGGLE_SIZE,
              height: TOGGLE_SIZE,
              // Opt out of the theme's 44 px touch-target minimum (#2719): the
              // toggle is desktop-only and its border makes the hit area visible.
              minWidth: TOGGLE_SIZE,
              minHeight: TOGGLE_SIZE,
              border: 1,
              borderColor: 'divider',
              borderRadius: '50%',
              bgcolor: darkSurfaces.sidebar,
              color: 'text.secondary',
              zIndex: (theme) => theme.zIndex.drawer + 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
          </Tooltip>
        </>
      )}

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Header />

        {/* Page content */}
        <Box
          id="main-content"
          sx={{
            flexGrow: 1,
            p: isFullBleed ? 2 : { xs: 2, sm: 3 },
            mt: `${String(HEADER_HEIGHT)}px`,
            mb: isMobile ? '64px' : 0, // Space for mobile nav
            pb: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : 0,
            backgroundColor: 'background.default',
            overflowX: 'hidden',
            overflowY: fullBleedDesktop ? 'hidden' : 'auto',
            maxWidth: '100%',
            minWidth: 0,
            // Fixed-height flex column so full-bleed pages fill the viewport
            // below the header and manage their own scrolling.
            ...(fullBleedDesktop && {
              display: 'flex',
              flexDirection: 'column',
              height: `calc(100vh - ${String(HEADER_HEIGHT)}px)`,
            }),
          }}
        >
          <Box
            sx={{
              maxWidth: isFullBleed ? '100%' : fullWidthLayout ? '100%' : 1800,
              mx: 'auto',
              width: '100%',
              ...(fullBleedDesktop && {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }),
            }}
          >
            <Breadcrumbs />
            <OnboardingPrompt />
            {children}
          </Box>
        </Box>
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Box component="nav" aria-label={tc('mobileNavigation')}>
          <MobileNav />
        </Box>
      )}

      {/* AI Assistant */}
      <AssistantContextProvider>
        <AssistantPanel />
      </AssistantContextProvider>

      {/* Session timeout handler */}
      <SessionTimeout />

      {/* New version notice and "What's new" (#3315) */}
      <VersionUpdateNotice />
      <WhatsNewDialog />
    </Box>
  );
}
